<template>
  <div class="builder-wrapper" v-click-outside="onClickOutsideQuery">
    <div v-if="queryFocusDetailsText !== ''" class="query-focus-tooltip" v-html="queryFocusDetailsText"></div>

    <span v-if="showGenerateQuery" v-tooltip="$t('booleanSearch.queries.fields.standard_query.after_filter_tooltip')" class="generate-query right-actions q-hl-teal fad fa-magic" @click="generateQuery"></span>
    <span class="copy-to-clipboard right-actions q-hl-teal fad fa-clipboard" @click="copyToClipboard"></span>

    <auto-complete-suggestion-box
      v-show="matchQuery"
      :sorted-queries="getQueriesFlatArray()"
      :match-query="matchQuery"
      :caret-coords="caretCoords"
      :isOpen="isAutoCompleteOpen"
      @selectQuery="selectQuery"
      @reset="resetSuggestionBox"
      @preventDefaultEscape="preventDefaultEscape"
      @toggleAutoCompleteOpen="toggleAutoCompleteOpen"
    />

    <div class="editor-wrapper" :class="{hasError: !isQueryValid}">
      <div ref="editor-sizing" class="editor-sizing" :style="{ height: queryInputHeight, paddingRight: queryPaddingRightPx }"
           v-html="sizingHtml"
           @scroll="setSameScroll"></div>
      <div ref="editor-syntax" class="editor-syntax" :style="{ height: queryInputHeight, paddingRight: queryPaddingRightPx }"
           v-html="highlightedHtml"
           @scroll="setSameScroll"></div>
      <textarea ref="editor-input" class="editor-input"
                :class="[{isChinese: isChinese}]"
                :style="{ height: queryInputHeight, paddingRight: queryPaddingRightPx }"
                :value="queryValue"
                :disabled="disabled"
                @focus="onFocus"
                @blur="onBlur"
                @input="updateQueryValue($event, $event.target.value)"
                @keyup="onQueryInput"
                @keyup.esc="resetSuggestionBox"
                @keydown="onKeyDown"
                @keydown.enter="(e) => { e.preventDefault(); }"
                @scroll="setSameScroll"
                @paste="onQueryPaste"
                @click="onQueryClick"
                @dblclick="onQueryDoubleClick"
      ></textarea>
      <div v-if="debugMode">{{queryJSON}}</div>
    </div>
    <div class="info-section">
      <div :class="{error: !isQueryValid, validation: true}">{{validationMessage}}</div>
      <span @click="toggleShowQueryInfoModal(true)" class="info-btn fas fa-info-circle" />
    </div>
    <info-modal v-if="showQueryInfoModal" @closeModal="toggleShowQueryInfoModal" />
  </div>
</template>

<script>
  //Config
  import { DEFAULT_OPTIONS, booleanOperators } from '../../../config/bool-query-validation-const';
  import { autocompleteOptionsOrder } from '../../../config/bool-const';

  //Helpers
  import domHelper from '../../../helpers/domHelper';
  import { setCaretPosition, getCursorXYCoordinates } from '../../../helpers/caret';
  import { maxString } from '../../../helpers/string';

  // Plugins
  import { mapActions, mapState } from 'vuex';
  import { cloneDeep, isEmpty, isEqual, isFunction } from 'lodash';
  import i18n from '../../../i18n';

  //Components
  import autoCompleteSuggestionBox from './AutoCompleteSuggestionBox.vue'
  import infoModal from './info-modal';

  export default {
    name: 'query-builder',

    mounted() {
      this.queryValue = this.model[this.queryKey] || '';
      this.caretPosition = this.queryValue.length;

      //initEditorHighlight -> initSyntaxHighlight + setSameScroll + checkEditorWidth
      this.initEditorHighlight();

      this.inputEditor = this.$refs['editor-input'];
      this.syntaxEditor = this.$refs['editor-syntax'];

      //Initiating observer which is needed to detect textarea resize
      if(this.$refs['editor-input']) {
        this.queryInputHeight = this.queryValue.trim().length > 0 && !this.fixedHeight ? '400px' : '100px';

        this.initObserver();

        window.addEventListener("resize", this.initEditorHighlight);
      }

      //init history list in case of empty query
      if(this.model[this.queryKey] === '') {
        this.undoHistoryArray.push({
          queryValue: '',
          caretPosition: 0
        });
      }
    },

    destroyed () {
      if (this.observer) this.observer.disconnect();
      window.removeEventListener("resize", this.initEditorHighlight);
    },

    props: {
      id: {
        type: String,
        required: true
      },
      model: {
        type: Object,
        default: () => {}
      },
      queryKey: {
        type: String,
        required: true
      },
      fixedHeight: {
        type: Boolean,
        default: () => false
      },
      disabled: {
        type: Boolean,
        default: () => false
      },
      generateQuery: Function
    },

    data() {
      return {
        focusTextEditor: [],
        defaultQueryPaddingRightPx: '10px',
        queryPaddingRightPx: '10px',
        queryValue: '',
        highlightedHtml: '',
        sizingHtml:'',
        isQueryValid: true,
        validationMessage: this.$t('booleanSearch.query_validation.no_error'),
        observer: null,
        queryInputHeight: null,
        debugMode: false,
        queryJSON: '',
        showQueryInfoModal: false,
        inputEditor: null,
        caretPosition: 0,
        matchQuery: null,
        autocompleteQueries: {
          field: [],
          group: [],
          operator: [],
          source_country:[]
        },
        caretCoords: {
          x: null,
          y: null
        },
        isElementInFocus: false,
        preventEscape: false,
        isAutoCompleteOpen: false,
        autoCompleteRequestedText: "",
        undoHistoryArray: [],
        allowUpdate: true,
        historyPosition: 0
      }
    },

    computed: {
      ...mapState({
        queryValidity: state => state.booleanSearch.query_validity
      }),
      isTagSearch () {
        return this.$route.meta.isTagSearchType
      },
      autoCompleteHasOptions() {
        return Object.keys(this.autocompleteQueries).find((key) => this.autocompleteQueries[key].length)
      },
      queryFocusDetailsText () {
        let value = '';
        if(this.focusTextEditor.length > 0) {
          value = [];
          this.focusTextEditor.forEach(focus => {
            const { originalText, originalTextStartIndex, originalTextEndIndex } = focus;
            let groupText = originalText.substr(originalTextStartIndex +1, originalTextEndIndex -1)
            value.push(`(${maxString(groupText, 10, '...')})`);
          });
          value = value.join('<span class="q-hl-orange fas fa-chevron-right" style="margin: 0 10px"></span>');
        }
        return value;
      },
      sortedQueries () {
        return Object.entries(this.autocompleteQueries).map(([key, value]) => {
          value.forEach(item => item.key = key)
          return value
        }).flat() || []
      },
      isChinese () {
        return !!(this.queryValue && this.queryValue.match(/[\u4E00-\u9FFF]/g));
      },
      showGenerateQuery() {
        return this.generateQuery && isFunction(this.generateQuery);
      }
    },

    methods: {
      ...mapActions({
        validateQuery : 'booleanSearch/validateQuery',
        autocompleteQuery: 'booleanSearch/autocompleteQuery'
      }),

      selectQuery(query) {
        let wordBounds = {};
        if(this.autoCompleteRequestedText && this.autoCompleteRequestedText.trim() !== '') {
          let fromIndex = this.caretPosition - this.autoCompleteRequestedText.length;
          wordBounds.start = this.queryValue.indexOf(this.autoCompleteRequestedText, fromIndex);
          wordBounds.end = wordBounds.start + this.autoCompleteRequestedText.length;
        }
        else{
          wordBounds = this.getWordBoundsAtPosition(this.queryValue.replace(/<br \/>/g, "      "), this.caretPosition);
        }

        let injectValue = query;
        const { start, end } = wordBounds;

        // take care of missing end bracket
        if(this.queryValue.slice(end - 1).startsWith(")")) injectValue = injectValue + ')'
        if(this.queryValue.slice(start).startsWith("(")) injectValue = '(' + injectValue
        let value = this.queryValue.slice(0, start) + injectValue + this.queryValue.slice(end);

        this.updateQueryValue(null, value)
        this.$nextTick(() => {
          this.initSyntaxHighlight(this.queryValue, {checkSpacing: true});
          setCaretPosition(this.inputEditor, start + injectValue.length);
        });
        this.resetAutocompleteQueries()
        this.toggleAutoCompleteOpen(false);
      },

      resetAutocompleteQueries() {
        this.autocompleteQueries = { field: [], group: [], operator: [], source_country: []}
      },

      resetSuggestionBox(setCaret=true) {
        this.resetAutocompleteQueries()
        this.toggleAutoCompleteOpen(false);
        this.matchQuery = null
        if(setCaret) setCaretPosition(this.inputEditor, this.caretPosition)
      },

      updateQueryValue(e, value, caretPosition) {
        if(!caretPosition) caretPosition = e && e.target.selectionStart
        this.caretPosition = caretPosition

        this.queryValue = value;

        const {top, left} = getCursorXYCoordinates(this.inputEditor, this.caretPosition);
        this.caretCoords.x = left
        this.caretCoords.y = top

        if(this.focusTextEditor.length > 0) {
          for(let i = this.focusTextEditor.length; i > 0; i--) {
            const { originalText, originalTextStartIndex, originalTextEndIndex } = this.focusTextEditor[i - 1];
            value = originalText.substr(0, originalTextStartIndex) + '(' + value + ')' + originalText.substr(originalTextEndIndex, originalText.length);
          }
        }

        this.initEditorHighlight();

        let newModel = cloneDeep(this.model);
        newModel[this.queryKey] = value;
        this.$emit('updateModel', newModel, { name: this.queryKey });
        this.$emit('onChange', value);
      },

      onClickOutsideQuery() {
        if(this.isElementInFocus) {
          this.resetSuggestionBox(false);
          this.isElementInFocus = false;
        }
      },

      copyToClipboard() {
        const copyText = this.$refs['editor-input'];
        domHelper.copyToClipboard(copyText);
      },

      onFocus () {
        if(!this.isElementInFocus) this.isElementInFocus = true;

        this.queryInputHeight = this.queryValue.trim().length > 0 || this.fixedHeight ? this.queryInputHeight : '400px';
        this.$emit('focus');
      },

      onBlur (e) {
        this.queryInputHeight = this.queryValue.trim().length > 0 && !this.fixedHeight ? this.queryInputHeight : '100px';
        this.$emit('onBlur', e);
      },

      onKeyDown (e) {
        const caretPosition = e && e.target.selectionStart;
        const hasSpaceBefore = this.queryValue.substr(caretPosition-1, 1) === ' ';
        const hasSpaceAfter = this.queryValue.length > caretPosition && this.queryValue.substr(caretPosition, 1) === ' '
        if(e.keyCode === 32 && (hasSpaceBefore || hasSpaceAfter)) {
          e.preventDefault();
          return;
        }

        const isUndo = e.keyCode == 90 && (e.ctrlKey || e.metaKey) && !e.shiftKey;
        const isRedo = (e.keyCode == 90 && e.metaKey && e.shiftKey) || (e.keyCode == 89 && e.ctrlKey);

        if(isUndo || isRedo) {
          e.preventDefault();

          let newPosition = isUndo ? this.historyPosition - 1 : this.historyPosition + 1;
          if(newPosition < 0 || newPosition > this.undoHistoryArray - 1 || !this.undoHistoryArray[newPosition]) return;

          this.allowUpdate = false;
          this.historyPosition = newPosition;

          const historyEntry = this.undoHistoryArray[newPosition];
          this.updateQueryValue(e, historyEntry.queryValue, historyEntry.caretPosition);
          this.focusTextEditor = historyEntry.focusTextEditor;

          this.$nextTick(() => {
            setCaretPosition(this.inputEditor, historyEntry.caretPosition);
          });
        }
      },

      onQueryInput(e) {
        const ignoreKeys = [37,38,39,40]; // 40 down, 38 up

        if (e.keyCode === 40 && this.matchQuery && this.autoCompleteHasOptions) return this.inputEditor.blur()

        if(ignoreKeys.indexOf(e.keyCode) > -1) {
          if([37, 39].indexOf(e.keyCode) > -1) {
            this.matchQuery = null;
            this.toggleAutoCompleteOpen(false);
          }

          return;
        }

        this.matchQuery = null

        const hasFocus = this.focusTextEditor && this.focusTextEditor.length > 0;
        //If ESC
        if(e.keyCode === 27 && hasFocus && !this.preventEscape){
          const { originalText, originalTextStartIndex, originalTextEndIndex } = this.focusTextEditor[this.focusTextEditor.length - 1];
          const newValue = originalText.substr(0, originalTextStartIndex) + '(' + this.queryValue + ')' + originalText.substr(originalTextEndIndex, originalText.length);
          this.caretPosition = newValue.length;
          this.focusTextEditor.pop();
          this.queryValue = newValue;
        }

        if(e.keyCode !== 27 || (e.keyCode === 27 && hasFocus && !this.preventEscape)) {
          this.autoCompleteRequestedText = '';
          this.initEditorHighlight();

          if(this.matchQuery) {
            let match = this.resolveMatch(this.matchQuery)
            if (match && match.trim().startsWith("(")) match = match.replace(/^[(]*/gi, '');
            if (match && match.trim().endsWith(")")) match = match.replace(/[)]*$/gi, '');
            this.autoCompleteRequestedText = match;

            let isGroupType = this.model.search_type ? false : true;
            this.autocompleteQuery({
              query: match,
              searchType: this.model.search_type,
              isMulti: this.model.is_multi,
              isStandardQuery: this.id === 'standard_query' && ['twitter', 'twitter_search_v3'].indexOf(this.model.search_type) > -1 || false,
              groupId: isGroupType ? this.model.id : false
            })
              .then((response) => {
                let data = response && response.data || {}
                let orderedData = {};

                if(!isEmpty(data)) {
                  autocompleteOptionsOrder.map(key => {
                    if(data[key]) {
                      orderedData[key] = data[key];
                      delete data[key];
                    }
                  })
                }

                this.autocompleteQueries = {...orderedData, ...data}

                if(this.getQueriesFlatArray().length > 0) {
                  this.toggleAutoCompleteOpen(true);
                }
              })
          }
        }

        if(this.preventEscape) this.preventDefaultEscape(false);
      },

      resolveMatch(match) {
        let matchText = match.match;
        let startPos = match.start;

        if(match.type === 'phrase') {
          const matchString = match.match && match.match.trim();
          const startsWithPara = matchString.startsWith('"')
          const endsWithPara = matchString.endsWith('"')
          if(startsWithPara)  matchText = match.match;

          //check if str contains quote
          let stringToCaret = this.queryValue.substr(0, this.caretPosition);
          const quotes = stringToCaret.match(/^"|[^\\](\\{2})*"/g);
          if(quotes && quotes.length % 2 !== 0) {
            let bounds =  this.getNearestQuote(this.queryValue, this.caretPosition);
            matchText = bounds.word;
            startPos = bounds.start;
          }
          else if(!startsWithPara) {
            // get an exact match of word in a string of multiple words
            // since we need to find spaces replace br if it has a space
            let bounds = this.getWordBoundsAtPosition(this.queryValue.replace(/<br \/>/g, "      "), this.caretPosition);
            matchText = bounds.word;
            startPos = bounds.start;
          }
        }
        else if(match.type === 'boolean_operator' && match.match.startsWith(' ')) {
          //the boolean_validator is sometimes capturing the space before operator itself
            matchText = match.match.trim();
            startPos = match.start + 1;
        }

        if(match.type === 'phrase' || match.type === 'single_term')
        {
          let string = this.queryValue.substr(0, startPos);
          let fieldRegex = new RegExp('(\\S+)([^\\\\]:)$', 'g');

          const isField = string.match(fieldRegex);
          if(isField) {
            matchText = isField[0] + matchText;
          }
        }

        return matchText;
      },

      isQuoteEscaped(str, position) {
        let previousChars = str.substr(0, position-1);
        return previousChars.match(/[^\\](\\{2})*"$/g)
      },

      getNearestQuote(str, position) {
        const isQuote = (c) => /"/.exec(c);
        let start = position - 1;
        let end = position;

        while (start >= 0 && (!isQuote(str[start]) ||
          (isQuote(str[start]) && this.isQuoteEscaped(str, start))
        )) {
          start -= 1;
        }
        start = Math.max(0, start);

        while (end < str.length && (!isQuote(str[end]) ||
          (isQuote(str[end]) && this.isQuoteEscaped(str, end))
          )) {
          end += 1;
        }
        end = Math.max(start, end);

        let word = str.substr(start, end - (start -1))

        if(word.endsWith(")")) word = word.slice(0, -1);

        return { word, start, end };
      },

      getWordBoundsAtPosition(str, position) {
        const isSpace = (c) => /\s/.exec(c);
        let start = position - 1;
        let end = position;

        while (start >= 0 && !isSpace(str[start])) {
          start -= 1;
        }
        start = Math.max(0, start + 1);

        while (end < str.length && !isSpace(str[end])) {
          end += 1;
        }
        end = Math.max(start, end);

        let word = str.substring(start, end)

        if(word.endsWith(")")) word = word.slice(0, -1)

        return {
          word,
          start,
          end
        }
      },

      onQueryPaste(e) {
        var clipboardData = (e.originalEvent || e).clipboardData || window.clipboardData;
        let plainText = clipboardData.getData('text/plain');
        if(plainText.indexOf('  ') > -1) { plainText = plainText.replace(/ {2,}/gi, ' ');}

        var success = window.document.execCommand('insertHtml', false, plainText);
        if (success) {
          e.preventDefault();
        }

        this.setSameScroll();
      },

      onQueryClick(e) {
        this.caretPosition = e.target.selectionStart
        this.matchQuery = null
        this.toggleAutoCompleteOpen(false);
        domHelper.removeCssClass('.highlight-bracket', 'highlight-bracket', this.$refs['editor-syntax']);
        const elementId = this.getElementToHighlight(e);
        if(elementId) domHelper.addCssClass('#' + elementId, 'highlight-bracket', this.$refs['editor-syntax']);
      },

      onQueryDoubleClick(e) {
        if(this.queryValue.trim() === '') return;

        const editorInput = this.$refs['editor-input'];
        const editorSyntax = this.$refs['editor-syntax'];

        const inputSelectionStartIndex = editorInput.selectionStart;
        let inputSelectionStartText = this.queryValue.substr(0, inputSelectionStartIndex);
        let openBracketIndex = inputSelectionStartText.lastIndexOf('(');

        //Match how
        if(openBracketIndex < inputSelectionStartText.lastIndexOf(')')){
          //replace brackets inside quotes
          const sqlStringQuotes = inputSelectionStartText.match(/((([^\\](\\{2})*")|(^(\\{2})")|^").*?[^\\](\\{2})*")/gi);
          sqlStringQuotes.forEach(quote => {
            quote = quote.startsWith('"') ? quote : quote.substring(quote.indexOf('"'));
            if(quote.match(/([(|)])/gi)){
              let newString = quote.replace(/([(|)])/gi, '.');
              inputSelectionStartText = inputSelectionStartText.replace(quote, newString);
            }
          });

          let openBrackets = this.sortMatches(this.getMatchedHighlighters([{name: 'open_bracket', type: 'grouping', regex: /([(])/g}], inputSelectionStartText, 'open bracket'));
          const closeBrackets = this.sortMatches(this.getMatchedHighlighters([{name: 'close_bracket', type: 'grouping', regex: /([)])/g}], inputSelectionStartText, 'close bracket'));

          if(closeBrackets.length > 0 && openBrackets.length > closeBrackets.length){
            //remove opening brackets that have a closing brackets
            closeBrackets.forEach(closing => {
              const possibleMatches = openBrackets.filter(opening => opening.start < closing.start)
              const matchedIndex = openBrackets.indexOf(possibleMatches.pop());
              if(matchedIndex > -1) openBrackets.splice(matchedIndex, 1)
            });
          }
          openBracketIndex = openBrackets[openBrackets.length - 1].start;
        }

        const elementId = this.getElementToHighlight(e, {ignoreClass: "not_double_clickable"});
        if(!elementId) return;

        const elementToEdit = editorSyntax.querySelector('#'+elementId);
        const textToEdit = elementToEdit && domHelper.htmlToPlainText(elementToEdit.innerHTML);
        const closingBracketIndex = textToEdit ? textToEdit.length : 0;

        this.focusTextEditor.push({
          originalText: this.queryValue,
          originalTextStartIndex: openBracketIndex,
          originalTextEndIndex: openBracketIndex + closingBracketIndex
        });

        this.queryValue = textToEdit.substr(1, textToEdit.length - 2);

        //Highlight
        this.initSyntaxHighlight(this.queryValue, { checkSpacing: false });
        this.setSameScroll();
      },

      setSameScroll(e){
        const editorInput = this.$refs['editor-input'];
        const editorSyntax = this.$refs['editor-syntax'];
        const editorSizing = this.$refs['editor-sizing'];


        let focusElement = editorInput;
        if(e) {
          focusElement = e.target;
        }

        editorInput.scrollTop = focusElement.scrollTop;
        editorSyntax.scrollTop = focusElement.scrollTop;
        editorSizing.scrollTop = focusElement.scrollTop;

        if(editorInput.scrollTop !== editorSyntax.scrollTop) {
          this.highlightedHtml += '<br />';
          editorSyntax.scrollTop = editorInput.scrollTop;
          this.sizingHtml += '<br />';
          editorSizing.scrollTop = editorInput.scrollTop;
        }
      },

      matchQueryItem(filteredMatches) {
        this.matchQuery = filteredMatches.find(match => {
          let isItAMatch = this.caretPosition - 1 >= match.start && this.caretPosition <= (match.start + match.length)
          if(isItAMatch && match.name !== 'new_line') {
            return match.isMatch = true
          }
        })
      },

      initObserver () {
        const queryInputElement = this.$refs['editor-input'];
        const vm = this;
        const config = { attributes: true };

        // create the observer
        const observer = new MutationObserver(function (mutations) {
          mutations.forEach(function (mutation) {
            // check if the mutation is of type attributes and update queryInputHeight if it is
            if (mutation.type === "attributes") {
              let { height, paddingTop, paddingBottom } = queryInputElement.style;

              if(height && vm.queryInputHeight !== height) {
                vm.queryInputHeight = (domHelper.pxToInt(height) - domHelper.pxToInt(paddingTop) - domHelper.pxToInt(paddingBottom)) + 'px';
              }
            }
          });
        });

        // observe element's specified mutations
        observer.observe(queryInputElement, config);
        // add the observer to data so we can disconnect it later
        this.observer = observer;
      },

      toggleShowQueryInfoModal (open) {
        this.showQueryInfoModal = open;
      },

      initEditorHighlight(){
        //Highlight
        this.initSyntaxHighlight(this.queryValue ? this.queryValue : '', { checkSpacing: false });
        this.setSameScroll();
        this.$nextTick().then(this.checkEditorWidth);
      },

      initSyntaxHighlight (content, options) {
        if(this.debugMode) console.log('----------------------------------------------------------------------------------------')
        const sqlString = content;

        if(sqlString.length === 0){
          this.highlightedHtml = '';
          this.sizingHtml = '';
          this.isQueryValid = true;
          this.validationMessage = null;
          return;
        }
        if(this.debugMode) console.log("Init Highlight sqlString length:", sqlString.trim().length)
        options = Object.assign({}, DEFAULT_OPTIONS, options)

        const matches = this.getMatchedHighlighters(options.highlighters, sqlString, 'highlighter match')
        const sortedMatches = this.sortMatches(matches);

        // filter/exclude nested matches (matches within the last match)
        let filteredMatches = []
        let upperBound = 0
        for (let i = 0; i < sortedMatches.length; i++) {
          if (sortedMatches[i].start >= upperBound) {
            filteredMatches.push(sortedMatches[i])
            upperBound = sortedMatches[i].start + sortedMatches[i].length
          }
        }

        //Check for any strings not in any quotes (any remaining characters not grouped yet)
        const missedMatched = this.getMissedMatches(options.stringHighlighters, filteredMatches, sqlString);
        if(missedMatched) filteredMatches = this.sortMatches(filteredMatches.concat(missedMatched)) || filteredMatches;

        //Remove any empty matches
        filteredMatches = filteredMatches.filter(function(m){return m.type === 'html_parsing' || (m.match && m.match.trim().length > 0)})

        if(this.debugMode) console.log('filteredMatches', filteredMatches)


        this.matchQueryItem(filteredMatches)

        const generatedJson = this.generateJson(filteredMatches);
        this.queryJSON = (JSON.stringify(generatedJson)).replace(/},/gi, '}, <br />');

        let { isQueryValid, validationMessage } = this.validations(sqlString, generatedJson);
        if(sqlString === ' ') return
        if (isQueryValid && this.queryValue.trim() !== '') {
          this.validateQuery({type: this.queryKey, model: this.model, isTagSearch: this.isTagSearch })
            .then(() => {
              const valid = this.queryValidity;

              isQueryValid = valid.isValid;
              if(valid.queryType && valid.queryType === this.queryKey && !valid.isValid) {
                if(valid.errorMessage) validationMessage = valid.errorMessage;
                else validationMessage = this.$t('booleanSearch.query_validation.generic_error');
              }

              this.isQueryValid = isQueryValid;
              this.validationMessage = validationMessage;
            })
            .catch(() => { this.isQueryValid = false; this.validationMessage = this.$t('booleanSearch.query_validation.generic_error'); });
        }
        else {
          this.isQueryValid = isQueryValid;
          this.validationMessage = validationMessage;
        }

        //Start Highlighting Text
        this.highlightedHtml = this.highlightString(options, sqlString, generatedJson);
        this.sizingHtml = this.highlightString(options, sqlString, [generatedJson[generatedJson.length - 1]]);
      },

      getMatchedHighlighters(highlighters, str){
        str = domHelper.clearFont(str);
        //if there are any <br />'s replace with \n as textarea only works with \n and <br />'s are giving incorrect caret position
        str = str.replace(/<br \/>/g, "\n")

        const matches = [];
        for (const hl of highlighters) {
          let match;
          // This is probably the one time when an assignment inside a condition makes sense
          // eslint-disable-next-line no-cond-assign
          while (match = hl.regex.exec(str)) {
            let matchText = hl.trimEnd ? match[0].substr(0, match[0].length - hl.trimEnd) : match[0];
            let start = match.index;

            /*
             * the phrase and field regex also captures the letter preceding the quote/keyword this is not really part
             * of the match needed, therefore if phrase and the first letter is colon or space or if field and first
             * letter is space, remove from match text and reset start position
             */
            if(['phrase', 'field'].indexOf(hl.type) !== -1 && /^(:|\\s| )/.test(matchText)) {
              if(hl.type === 'phrase') matchText = matchText.replace(/^(:|\\s| )/, '');
              else if(hl.type === 'field') matchText = matchText.replace(/^(\\s| )/, '');
              start++;
            }

            matches.push({
              name: hl.name,
              type: hl.type,
              class: hl.class,
              start: start,
              length: matchText.length,
              match: matchText,
            });
          }
        }

        return matches;
      },

      sortMatches(matches){
        return matches.slice().sort((a, b) => a.start - b.start)
      },

      getMissedMatches(highlighters, filteredMatches, sqlString){

        //Check for string keywords without ""
        let matchedLowerBound = 0;
        let matchedUpperBound = 0;
        const missedStrings = [];
        for (let i = 0; i < filteredMatches.length; i++) {
          const match = filteredMatches[i]
          if(matchedLowerBound === match.start || matchedUpperBound === match.start || match.start - matchedUpperBound < 1 ||
            (match.start - matchedUpperBound === 1 && sqlString[matchedUpperBound] === ' ')){
            matchedLowerBound = match.start;
            matchedUpperBound = match.start + match.length;
          } else {
            const missedMatchStart = matchedUpperBound;
            const missedMatchLenght = match.start - matchedUpperBound;

            missedStrings.push(this.getMissedMatchObj(missedMatchStart, missedMatchLenght, highlighters, sqlString));

            matchedLowerBound = match.start;
            matchedUpperBound = match.start + match.length;

          }
        }

        //If there are more characters after we finished all matches, add to missed matches as string
        if(sqlString.length > matchedUpperBound){
          const missedMatchStart = matchedUpperBound;
          const missedMatch = sqlString.substr(missedMatchStart, sqlString.length);
          const missedMatchLenght = missedMatch.length;

          missedStrings.push(this.getMissedMatchObj(missedMatchStart, missedMatchLenght, highlighters, sqlString));
        }

        if(this.debugMode) console.log('missedStrings', missedStrings)

        return missedStrings;
      },

      getMissedMatchObj(missedMatchStart, missedMatchLength, highlighters, sqlString){
        const stringHighlighters = this.getMatchedHighlighters(highlighters, sqlString.substr(missedMatchStart, missedMatchLength), 'wildcard highlighter match');
        const wildcard = stringHighlighters && stringHighlighters.length > 0 ? { wildcard: stringHighlighters } : {};
        const matchText = sqlString.substr(missedMatchStart, missedMatchLength);
        return {name: 'string', type: matchText.indexOf(' ') > -1 ? 'phrase' : 'single_term', start: missedMatchStart, length: missedMatchLength, match: matchText, ...wildcard};
      },

      generateJson(matches){
        const cloneMatches = cloneDeep(matches);
        let newJsonMatches = [];

        let currentParents = [];
        let matchClasses = false;
        while(cloneMatches.length > 0){
          const match = cloneMatches[0];
          const index = newJsonMatches.length;

          match.id = index;

          if (match.name === 'open_bracket') {
            currentParents.push(match);
            matchClasses = (matchClasses ? matchClasses + ' ' : '') + 'bracket-' + match.id;
            if(match.class) {
              matchClasses += " " + match.class;
            }
          }

          if(matchClasses)
            match.classList = matchClasses;

          if(currentParents && currentParents.length > 0)
            match.parent = 'bracket-' + currentParents[currentParents.length - 1].id;

          if (match.name === 'close_bracket'){
            currentParents.pop();
            matchClasses = false;
          }

          newJsonMatches.push(match);

          cloneMatches.shift();
        }

        return newJsonMatches;
      },

      validations(sqlString, json){
        let queryValidity = { isQueryValid: true, validationMessage: this.$t('booleanSearch.query_validation.no_error') };
        /*
         1. Opening and Closing brackets count must match
         2. Query cannot start with * or ?
         3. Cannot have boolean operators consecutively (AND/OR/NOT)
         4. Query cannot start with boolean operators (AND/OR/NOT)
         */
        if(sqlString === ' ') return

        //First Replace All Quotation Strings
        const sqlStringQuoteFree = sqlString.replace(/(^"|^(\\{2})"|([^\\])(\\{2})*").*?("|(\\{2})"|[^\\](\\{2})*")/gi, '$3quote_replaced');

        //Check Opening and Closing Brackets
        const openingBrackets = (sqlStringQuoteFree.match(/([(])/gi) || []).length;
        const closingBrackets = (sqlStringQuoteFree.match(/([)])/gi) || []).length;

        const quotes = (sqlStringQuoteFree.match(/(([^\\](\\{2})*")|(^(\\{2})")|^")/gi) || []).length;

        const startsWithOperatorRegex = new RegExp(`^(\\()*( )*(${booleanOperators.join('|')})`, 'g');
        const endsWithOperatorRegex = new RegExp(`(NOT|${booleanOperators.join('|')})( )*(\\))*$`, 'g');
        let startsWithOperator = (str) => startsWithOperatorRegex.exec(str);
        let endsWithOperator = (str) => endsWithOperatorRegex.exec(str);

        if(openingBrackets !== closingBrackets){
          queryValidity.isQueryValid = false;
          queryValidity.validationMessage = openingBrackets > closingBrackets ?
            i18n.t('booleanSearch.query_validation.missing_closing_brackets') :
            i18n.t('booleanSearch.query_validation.missing_opening_brackets');

        } else if(quotes % 2 !== 0){
          queryValidity.isQueryValid = false;
          queryValidity.validationMessage = i18n.t('booleanSearch.query_validation.missing_quotes');

        } else if(['*', '?'].indexOf(sqlStringQuoteFree.substr(0, 1)) > -1){
          queryValidity.isQueryValid = false;
          queryValidity.validationMessage = i18n.t('booleanSearch.query_validation.invalid_start_char');

        } else if(!this.validateQuerySyntax(json, 'type', 'boolean_operator')){
          queryValidity.isQueryValid = false;
          queryValidity.validationMessage = i18n.t('booleanSearch.query_validation.invalid_consecutive_operators');
        } else if((json[0].type === 'boolean_operator' && json[0].name.toLowerCase() !== 'not') || json[json.length -1].type === 'boolean_operator' || startsWithOperator(sqlString.trim()) || endsWithOperator(sqlString.trim())){
          queryValidity.isQueryValid = false;
          queryValidity.validationMessage = i18n.t('booleanSearch.query_validation.invalid_start_end_boolean_operators');
        }

        return queryValidity;
      },

      validateQuerySyntax(json, key, value){
        for(var i = 0; i <= json.length - 1; i++){
          const match = json[i];
          const nextMatch = json[i+1];

          if(match && nextMatch && match[key] === value && match[key] === nextMatch[key]){
            return false;
          }
        }

        return true;
      },

      highlightString(options, sqlString, filteredMatches){
        let highlighted = '';
        let currentParents = [];
        for (let i = 0; i < filteredMatches.length; i++) {
          const match = filteredMatches[i]
          const nextMatch = filteredMatches[i + 1]
          const prevMatch = i > 0 ? filteredMatches[i - 1] : false

          // console.log('match ', match)

          highlighted += this.highlightStringMatch(options, sqlString, currentParents, i, match, nextMatch, prevMatch)

          if (match.name === 'open_bracket') currentParents.push(match);
        }

        return highlighted.replace(/<br>/gi, '');
      },

      highlightStringMatch(options, sqlString, addedParents, i, match, nextMatch, prevMatch){

        let highlighted = '';

        if (i === 0 && match.start > 0) highlighted += sqlString.substr(0, match.start);

        let stringMatch = sqlString.substr(match.start, match.length);

        if(match.wildcard) stringMatch = this.highlightString({...options, idPrefix: 'q-hl-string-' + i + '-'}, stringMatch, match.wildcard);

        if(match.parent && !(addedParents && addedParents.length > 0 && addedParents.find(function(p){ return p.id == match.parent.replace('bracket-', '') }))) highlighted += `<span id="${match.parent}">`;

        const highlightOpeningSpan = `<span id="${options.idPrefix || options.classPrefix}${i}" data-parent="${match.parent || ''}" class="${match.classList ? match.classList + ' ' : ''}${options.classPrefix}${match.name}${match.isMatch? ' isMatch' : ''}">`;

        highlighted += `${highlightOpeningSpan}${domHelper.clearFont(stringMatch)}</span>`;

        if (match.parent && match.name === 'close_bracket') highlighted += `</span>`;

        if (nextMatch) {
          highlighted += sqlString.substr(match.start + match.length, nextMatch.start - (match.start + match.length));
        } else if (sqlString && sqlString.trim().length >= (match.start + match.length)) {
          highlighted += sqlString.substr(match.start + match.length)
        }

        return highlighted.replace(/<br>/gi, '');
      },

      getElementToHighlight(e, opts = {}){
        const elementInputX = e.clientX;
        const elementInputY = e.clientY;

        const editorSyntax = this.$refs['editor-syntax'];

        const children = editorSyntax.querySelectorAll('span');
        let matches = [];
        for (let i = 0; i < children.length; i++) {
          const child = children[i];
          const childExtraWidth = child.getAttribute('data-parent') ? 0 : 10;

          const childRects = child.getClientRects();

          for (let r = 0; r < childRects.length; r++) {
            const childRect = childRects[r];
            const xEndPos = childRect.x + childRect.width + childExtraWidth;
            const matchesX = elementInputX >= childRect.x && elementInputX <= xEndPos;
            const matchesY = elementInputY >= childRect.y && elementInputY <= childRect.y + childRect.height;
            if(matchesX && matchesY) {
              let xProximity = -(elementInputX - xEndPos);
              if(domHelper.hasCssClass(child, 'q-hl-close_bracket') && xProximity > elementInputX - childRect.x) xProximity = elementInputX - childRect.x;
              if(opts.ignoreClass && domHelper.hasCssClass(child, opts.ignoreClass)) {
                continue;
              }
              matches.push({
                element: child,
                rect: childRect,
                xProximity: xProximity,
                yProximity: -(elementInputY - (childRect.y + childRect.height))
              });
            }
          }
        }

        matches = matches.slice().sort((a, b) => a.yProximity - b.yProximity || a.xProximity - b.xProximity); // && b.yProximity - a.yProximity
        if(this.debugMode) console.log('matches', matches)
        for (let i = 0; i < matches.length; i++) {
          const match = matches[i];
          const child = match.element;
          const childRect = match.rect;
          const childExtraWidth = child.getAttribute('data-parent') ? 0 : 10;

          const bracketGroups = child.className.match(/bracket-[0-9]*/gi);
          if (match.xProximity <= (childRect.width + childExtraWidth) && match.yProximity <= childRect.height) {
            return bracketGroups ? bracketGroups[bracketGroups.length - 1] : child.getAttribute('data-parent') || false;
          } else if (bracketGroups) {
            return bracketGroups[bracketGroups.length - 1];
          } else if(child.getAttribute('data-parent')){
            return child.getAttribute('data-parent');
          }
        }
      },

      checkEditorWidth (){
        const editorInput = this.$refs['editor-input'];
        const editorSyntax = this.$refs['editor-syntax'];
        const editorSizing = this.$refs['editor-sizing'];

        if(!editorSyntax || !editorSizing) return;

        //Reset padding right
        this.queryPaddingRightPx = this.defaultQueryPaddingRightPx;
        this.setSameScroll();

        const editorSyntaxLastChild = editorSyntax.children[editorSyntax.children.length - 1];
        const editorSizingLastChild = editorSizing.children[editorSizing.children.length - 1];

        if(!editorSyntaxLastChild || !editorSizingLastChild) return;

        const syntaxLastRect = editorSyntaxLastChild.getBoundingClientRect();
        const sizingLastRect = editorSizingLastChild.getBoundingClientRect();
        if(Math.round(syntaxLastRect.bottom) !== Math.round(sizingLastRect.bottom) || Math.round(syntaxLastRect.right) !== Math.round(sizingLastRect.right)){
          let max = 30;
          let attempts = 0;
          while(attempts <= max && (Math.round(syntaxLastRect.bottom) !== Math.round(sizingLastRect.bottom) || Math.round(syntaxLastRect.right) !== Math.round(sizingLastRect.right))){
            let currentPadding = domHelper.pxToInt(this.queryPaddingRightPx) || 10;
            attempts++;
            const newPadding = (currentPadding + 1) + 'px';
            if(this.debugMode) console.log('editorSyntax resizing to padding:', newPadding);
            this.queryPaddingRightPx = newPadding;
            this.setSameScroll();
          }
        }
      },

      preventDefaultEscape (prevent) {
        this.preventEscape = prevent;
      },

      toggleAutoCompleteOpen (open) {
        if (this.isAutoCompleteOpen !== open) {
          this.isAutoCompleteOpen = open;
        }
      },

      getQueriesFlatArray (queries) {
        if(!queries) queries = this.autocompleteQueries;

        return Object.entries(queries).map(([key, value]) => {
          value.forEach(item => item.key = key)
          return value
        }).flat() || []
      }
    },

    watch: {
      isQueryValid: {
        handler (isValid) {
          this.$emit('updateQuerySyntaxValid', isValid);
        }
      },
      queryValue: {
        handler (newValue, oldValue) {
          if (oldValue !== newValue && this.allowUpdate) {
            if(this.undoHistoryArray.length - 1 > this.historyPosition) {
              this.undoHistoryArray.splice(this.historyPosition + 1);
            }

            this.undoHistoryArray.push({
              queryValue: newValue,
              caretPosition: this.caretPosition,
              focusTextEditor: cloneDeep(this.focusTextEditor)
            });
            this.historyPosition = this.undoHistoryArray.length -1;
          }
          else if(!this.allowUpdate) {
            this.allowUpdate = true;
          }
        }
      },
      model: {
        handler (newValue) {
          if(newValue[this.queryKey] && !isEqual(newValue[this.queryKey], this.queryValue)) {
            this.updateQueryValue(null, newValue[this.queryKey], newValue[this.queryKey].length)
          }
        },
        deep: true
      }
    },

    components: {
      autoCompleteSuggestionBox,
      infoModal,
    }
  }
</script>